import { useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactWidget = ({ open, setOpen }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) { toast.error("Please add your name and a message."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Please enter a valid email."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name, email, message });
      setSent(true);
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating action button */}
      <button onClick={() => setOpen(!open)} data-testid="contact-fab"
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-white text-black shadow-2xl flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm rounded-2xl bg-[#121212] border border-white/10 shadow-2xl overflow-hidden" data-testid="contact-panel">
          <div className="bg-white text-black px-5 py-4">
            <p className="font-heading text-lg tracking-tight">Chat with us</p>
            <p className="text-xs text-black/60">We usually reply within a business day.</p>
          </div>

          <div className="p-5">
            <div className="rounded-2xl rounded-tl-sm bg-white/5 border border-white/10 p-4 text-sm text-white/80 mb-4">
              Hi! 👋 Have a question about our photochromic readers? Leave a note and our team will get back to you.
            </div>

            {sent ? (
              <div className="text-center py-6" data-testid="contact-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                <p className="font-heading text-xl mt-4">Message sent!</p>
                <p className="text-white/60 text-sm mt-2">Thanks — we'll be in touch shortly.</p>
                <Button onClick={() => setSent(false)} data-testid="contact-new-message"
                  className="mt-5 rounded-full bg-white text-black hover:bg-white/85 h-10 px-6 text-sm font-semibold">
                  Send another
                </Button>
              </div>
            ) : (
              <form onSubmit={send} className="space-y-3">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="contact-name"
                  className="h-11 rounded-xl bg-black border-white/20 text-white placeholder:text-white/40 px-4 focus-visible:ring-white/30" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="contact-email-input"
                  className="h-11 rounded-xl bg-black border-white/20 text-white placeholder:text-white/40 px-4 focus-visible:ring-white/30" />
                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="How can we help?" rows={3} data-testid="contact-message"
                  className="rounded-xl bg-black border-white/20 text-white placeholder:text-white/40 px-4 py-3 focus-visible:ring-white/30 resize-none" />
                <Button type="submit" disabled={sending} data-testid="contact-send"
                  className="w-full rounded-full bg-white text-black hover:bg-white/85 h-11 font-semibold">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};
